import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search as SearchIcon, X, SlidersHorizontal, BadgeCheck } from "lucide-react";
import { searchAll, DEFAULT_LOCATION } from "@/lib/mockData";
import { EventCard } from "@/components/EventCard";
import { ServiceCard } from "@/components/ServiceCard";
import InitialAvatar from "@/components/InitialAvatar";

const TABS = [
    { id: "all",      label: "All" },
    { id: "events",   label: "Events" },
    { id: "services", label: "Services" },
    { id: "people",   label: "People" },
];

const PRICE_BUCKETS = [
    { id: "any",   label: "Any",        min: 0,       max: Infinity },
    { id: "low",   label: "Under ₦10k", min: 0,       max: 10_000 },
    { id: "mid",   label: "₦10k–50k",   min: 10_000,  max: 50_000 },
    { id: "high",  label: "₦50k–200k",  min: 50_000,  max: 200_000 },
    { id: "lux",   label: "₦200k+",     min: 200_000, max: Infinity },
];

const RATING_OPTIONS = [0, 3, 4, 4.5];

const Search = () => {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [tab, setTab] = useState("all");
    const [showFilters, setShowFilters] = useState(false);

    const [priceBucket, setPriceBucket] = useState("any");
    const [locationFilter, setLocationFilter] = useState("");
    const [minRating, setMinRating] = useState(0);
    const [verifiedOnly, setVerifiedOnly] = useState(false);

    const baseResults = useMemo(() => searchAll(q), [q]);

    const priced = (s) => {
        const b = PRICE_BUCKETS.find((x) => x.id === priceBucket) || PRICE_BUCKETS[0];
        const v = Number(s.price ?? s.priceFrom ?? 0);
        return v >= b.min && v <= b.max;
    };
    const locMatch = (s) => {
        if (!locationFilter.trim()) return true;
        return (s.location || "").toLowerCase().includes(locationFilter.trim().toLowerCase());
    };

    const filteredServices = baseResults.services
        .filter(priced)
        .filter(locMatch)
        .filter((s) => (s.rating || 0) >= minRating)
        .filter((s) => (verifiedOnly ? !!s.verified : true));

    const filteredEvents = baseResults.events
        .filter(locMatch);

    const filteredPeople = baseResults.people;

    const total = filteredServices.length + filteredEvents.length + filteredPeople.length;

    const show = {
        events: tab === "all" || tab === "events",
        services: tab === "all" || tab === "services",
        people: tab === "all" || tab === "people",
    };

    const activeFiltersCount =
        (priceBucket !== "any" ? 1 : 0) +
        (locationFilter.trim() ? 1 : 0) +
        (minRating > 0 ? 1 : 0) +
        (verifiedOnly ? 1 : 0);

    const resetFilters = () => {
        setPriceBucket("any");
        setLocationFilter("");
        setMinRating(0);
        setVerifiedOnly(false);
    };

    return (
        <div className="screen" data-testid="search-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60">
                <div className="flex items-center gap-2">
                    <button
                        data-testid="search-back-btn"
                        onClick={() => navigate(-1)}
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95"
                        aria-label="Back"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <label className="flex-1 flex items-center gap-2 h-12 px-4 rounded-full bg-secondary focus-within:bg-background focus-within:ring-2 focus-within:ring-primary/40 transition-[background-color,box-shadow]">
                        <SearchIcon size={16} className="text-muted-foreground" />
                        <input
                            data-testid="search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            autoFocus
                            placeholder="Search events, services, people"
                            className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/80"
                        />
                        {q && (
                            <button
                                data-testid="search-clear-btn"
                                onClick={() => setQ("")}
                                className="h-6 w-6 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                                aria-label="Clear"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </label>
                    <button
                        data-testid="search-filters-toggle"
                        onClick={() => setShowFilters((s) => !s)}
                        className={[
                            "h-10 w-10 relative flex items-center justify-center rounded-xl border transition-colors",
                            showFilters ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border text-foreground hover:bg-muted",
                        ].join(" ")}
                        aria-pressed={showFilters}
                        aria-label="Filters"
                    >
                        <SlidersHorizontal size={18} />
                        {activeFiltersCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[9.5px] font-bold flex items-center justify-center">
                                {activeFiltersCount}
                            </span>
                        )}
                    </button>
                </div>

                <div className="mt-3 flex gap-2 no-scrollbar overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`search-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={[
                                "shrink-0 h-8 px-3.5 rounded-full text-[12.5px] font-medium transition-colors",
                                tab === t.id
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-secondary text-secondary-foreground hover:bg-muted",
                            ].join(" ")}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {showFilters && (
                    <div className="mt-3 p-3 rounded-2xl border border-border bg-card space-y-3" data-testid="search-filters">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Price</div>
                            <div className="flex flex-wrap gap-2">
                                {PRICE_BUCKETS.map((b) => {
                                    const active = priceBucket === b.id;
                                    return (
                                        <button
                                            key={b.id}
                                            data-testid={`search-filter-price-${b.id}`}
                                            onClick={() => setPriceBucket(b.id)}
                                            className={[
                                                "h-8 px-3 rounded-full text-[11.5px] font-medium border",
                                                active
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-secondary text-foreground border-border hover:bg-muted",
                                            ].join(" ")}
                                        >
                                            {b.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Location</div>
                            <input
                                data-testid="search-filter-location"
                                value={locationFilter}
                                onChange={(e) => setLocationFilter(e.target.value)}
                                placeholder={`e.g. ${DEFAULT_LOCATION.city}`}
                                className="w-full h-10 px-3 rounded-xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[13.5px]"
                            />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Minimum rating</div>
                            <div className="flex flex-wrap gap-2">
                                {RATING_OPTIONS.map((r) => {
                                    const active = minRating === r;
                                    return (
                                        <button
                                            key={r}
                                            data-testid={`search-filter-rating-${r}`}
                                            onClick={() => setMinRating(r)}
                                            className={[
                                                "h-8 px-3 rounded-full text-[11.5px] font-medium border",
                                                active
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-secondary text-foreground border-border hover:bg-muted",
                                            ].join(" ")}
                                        >
                                            {r === 0 ? "Any" : `${r}★+`}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                data-testid="search-filter-verified"
                                checked={verifiedOnly}
                                onChange={(e) => setVerifiedOnly(e.target.checked)}
                                className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
                            />
                            <span className="text-[12.5px] inline-flex items-center gap-1">
                                <BadgeCheck size={12} className="text-primary" />
                                Verified only
                            </span>
                        </label>
                        {activeFiltersCount > 0 && (
                            <button
                                data-testid="search-filters-reset"
                                onClick={resetFilters}
                                className="text-[12px] font-medium text-primary"
                            >
                                Reset filters
                            </button>
                        )}
                    </div>
                )}
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar pb-8">
                {!q.trim() ? (
                    <EmptyHint />
                ) : total === 0 ? (
                    <NoResults q={q} />
                ) : (
                    <div className="pt-2">
                        {show.events && filteredEvents.length > 0 && (
                            <Section title="Events" testId="search-results-events">
                                <div className="px-5 grid grid-cols-1 gap-3">
                                    {filteredEvents.map((ev) => <EventCard key={ev.id} event={ev} />)}
                                </div>
                            </Section>
                        )}
                        {show.services && filteredServices.length > 0 && (
                            <Section title="Services" testId="search-results-services">
                                <div className="px-5 grid grid-cols-2 gap-3">
                                    {filteredServices.map((s) => <ServiceCard key={s.id} service={s} />)}
                                </div>
                            </Section>
                        )}
                        {show.people && filteredPeople.length > 0 && (
                            <Section title="People" testId="search-results-people">
                                <ul className="px-5 space-y-2.5">
                                    {filteredPeople.map((p) => (
                                        <li
                                            key={p.id}
                                            data-testid={`person-${p.id}`}
                                            className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card shadow-soft"
                                        >
                                            <InitialAvatar name={p.name} size={44} />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-display text-[14px] font-semibold truncate">{p.name}</div>
                                                <div className="text-[12px] text-muted-foreground truncate">
                                                    @{p.username} · {p.role}
                                                </div>
                                            </div>
                                            <span className="text-[11.5px] text-muted-foreground">{p.location}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const Section = ({ title, testId, children }) => (
    <section data-testid={testId} className="pt-4 pb-1">
        <h3 className="px-5 mb-2 font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {title}
        </h3>
        {children}
    </section>
);

const EmptyHint = () => (
    <div className="px-6 pt-14 text-center" data-testid="search-empty-hint">
        <div
            className="mx-auto h-14 w-14 rounded-full flex items-center justify-center"
            style={{
                background:
                    "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.06) 60%, transparent 100%)",
            }}
        >
            <SearchIcon size={22} className="text-primary" />
        </div>
        <p className="mt-4 font-display text-[15px] font-semibold">Start typing to search</p>
        <p className="text-[12.5px] text-muted-foreground mt-1">Find events, services or people near you.</p>
    </div>
);

const NoResults = ({ q }) => (
    <div className="px-6 pt-14 text-center" data-testid="search-no-results">
        <p className="font-display text-[15px] font-semibold">No matches for "{q}"</p>
        <p className="text-[12.5px] text-muted-foreground mt-1">Try a different keyword, adjust filters, or broaden your location.</p>
    </div>
);

export default Search;
